import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

const PostForComposerAsset = graphql(`
  fragment PostForComposerAsset on Asset @_unmask {
    __typename
    id
    source
    thumbnail
    ... on ImageAsset {
      image {
        altText
        width
        height
        userTags {
          handle
          x
          y
        }
      }
    }
    ... on VideoAsset {
      video {
        title
        durationMs
        width
        height
        fileSize
        audioCodec
        thumbnailOffset
      }
    }
    ... on DocumentAsset {
      document {
        title
        numPages
        filesize
      }
    }
  }
`)

const PostForComposerLinkAttachment = graphql(`
  fragment PostForComposerLinkAttachment on LinkAttachment @_unmask {
    url
    expandedUrl
    title
    text
    thumbnail
  }
`)

const PostForComposerAnnotation = graphql(`
  fragment PostForComposerAnnotation on Annotation @_unmask {
    type
    content
    text
    url
    indices
  }
`)

const PostForComposerThreadedPost = graphql(
  `
    fragment PostForComposerThreadedPost on ThreadedPost @_unmask {
      text
      assets {
        ...PostForComposerAsset
      }
      metadata {
        bluesky {
          linkAttachment {
            ...PostForComposerLinkAttachment
          }
        }
        threads {
          linkAttachment {
            ...PostForComposerLinkAttachment
          }
        }
      }
    }
  `,
  [PostForComposerAsset, PostForComposerLinkAttachment],
)

/**
 * Loads a post for the composer's edit and duplicate flows. Replaces the
 * legacy `getPost` RPC, which buffer-web serves through its `Update` models:
 * those cut text to the network limit on read (Bluesky at 300 characters).
 * `postToRpcUpdate` maps the result to the `RpcUpdate` shape the composer
 * still consumes.
 */
export const GetPostForComposer = graphql(
  `
    query GetPostForComposer($id: PostId!) {
      post(input: { id: $id }) {
        id
        status
        text
        dueAt
        createdAt
        isCustomScheduled
        schedulingType
        ideaId
        channelId
        channelService
        tags {
          id
          name
          color
        }
        assets {
          ...PostForComposerAsset
        }
        metadata {
          __typename
          ... on InstagramPostMetadata {
            type
            firstComment
            link
            shouldShareToFeed
            isAiGenerated
            collaborators
            trialReelGraduationStrategy
            geolocation {
              id
              text
            }
            stickerFields {
              text
              music
              products
              topics
              other
            }
            audio {
              id
              type
              title
              displayArtist
              creatorUsername
              duration
              audioVolume
              videoVolume
            }
          }
          ... on FacebookPostMetadata {
            type
            firstComment
            title
            annotations {
              ...PostForComposerAnnotation
            }
            linkAttachment {
              ...PostForComposerLinkAttachment
            }
          }
          ... on LinkedInPostMetadata {
            type
            firstComment
            annotations {
              ...PostForComposerAnnotation
            }
            linkAttachment {
              ...PostForComposerLinkAttachment
            }
          }
          ... on TwitterPostMetadata {
            type
            isAiGenerated
            retweet {
              id
              text
              url
              createdAt
              user {
                name
                username
                avatar
              }
            }
            thread {
              ...PostForComposerThreadedPost
            }
          }
          ... on PinterestPostMetadata {
            type
            title
            url
            board {
              id
            }
          }
          ... on GoogleBusinessPostMetadata {
            type
            title
            details {
              __typename
              ... on GoogleBusinessWhatsNewMetaData {
                button
                link
              }
              ... on GoogleBusinessOfferMetaData {
                title
                startDate
                endDate
                code
                link
                terms
              }
              ... on GoogleBusinessEventMetaData {
                title
                startDate
                endDate
                isFullDayEvent
                button
                link
              }
            }
          }
          ... on YoutubePostMetadata {
            type
            title
            privacy
            license
            notifySubscribers
            embeddable
            madeForKids
            isAiGenerated
            category {
              categoryId
              title
            }
          }
          ... on MastodonPostMetadata {
            type
            spoilerText
            thread {
              ...PostForComposerThreadedPost
            }
          }
          ... on StartPagePostMetadata {
            type
            link
          }
          ... on TiktokPostMetadata {
            type
            title
            isAiGenerated
          }
          ... on ThreadsPostMetadata {
            type
            topic
            locationId
            locationName
            linkAttachment {
              ...PostForComposerLinkAttachment
            }
            thread {
              ...PostForComposerThreadedPost
            }
          }
          ... on BlueskyPostMetadata {
            type
            linkAttachment {
              ...PostForComposerLinkAttachment
            }
            thread {
              ...PostForComposerThreadedPost
            }
          }
          ... on SubstackPostMetadata {
            type
            linkAttachment {
              ...PostForComposerLinkAttachment
            }
          }
        }
      }
    }
  `,
  [
    PostForComposerAsset,
    PostForComposerLinkAttachment,
    PostForComposerAnnotation,
    PostForComposerThreadedPost,
  ],
)

export type ComposerPost = ResultOf<typeof GetPostForComposer>['post']
